import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Grid3X3, Mail, Lock, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const result = await login(email, password);
        
        if (result.success) {
            toast.success('Welcome back!');
            navigate('/dashboard');
        } else {
            toast.error(result.error);
        }
        
        setIsLoading(false);
    };

    return (
        <div className="min-h-screen bg-slate-50 flex" data-testid="login-page">
            {/* Left Panel - Form */}
            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md animate-slide-up">
                    {/* Logo */}
                    <Link to="/" className="inline-flex items-center gap-2 mb-8">
                        <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                            <Grid3X3 className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-2xl font-bold text-slate-900 tracking-tight">AppShell</span>
                    </Link>

                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-2">
                            Welcome back
                        </h1>
                        <p className="text-slate-600">
                            Sign in to access your platform dashboard
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-slate-700">Email</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="you@company.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-10 h-12 border-slate-200 focus:border-indigo-600 focus:ring-indigo-600"
                                    required
                                    data-testid="login-email-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-slate-700">Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-10 h-12 border-slate-200 focus:border-indigo-600 focus:ring-indigo-600"
                                    required
                                    data-testid="login-password-input"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
                            disabled={isLoading}
                            data-testid="login-submit-btn"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                    Signing in...
                                </>
                            ) : (
                                <>
                                    Sign In
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </>
                            )}
                        </Button>
                    </form>
                </div>
            </div>

            {/* Right Panel - Branding */}
            <div className="hidden lg:flex flex-1 bg-indigo-600 items-center justify-center p-12">
                <div className="max-w-md text-center">
                    <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-8">
                        <Grid3X3 className="w-10 h-10 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-white tracking-tight mb-4">
                        Your Apps, One Platform
                    </h2>
                    <p className="text-indigo-100 leading-relaxed">
                        Access all your applications from a single dashboard. 
                        Navigate seamlessly between tools while maintaining a unified experience.
                    </p>
                    <div className="mt-8 flex justify-center gap-3">
                        <div className="w-3 h-3 bg-white/30 rounded-full"></div>
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                        <div className="w-3 h-3 bg-white/30 rounded-full"></div>
                    </div>
                </div>
            </div>
        </div>
    );
}
